/**
 * ReceiptScanModal — M13a-4 Beleg-Buchungs-Modal (KI-OCR → Expense).
 *
 * Spiegelt `ExpenseBookingModal` für den Scan-Pfad: Bild + vorbefüllte Felder
 * nebeneinander, feldweise Mensch-Bestätigung, KEIN Silent Booking. Der Draft
 * kommt aus `mapScannedReceiptToExpenseDraft` (T-2, E-2: `vatDeductible=false`
 * IMMER im Draft); die effektive §15-Abzugsfähigkeit berechnet erst dieses Modal
 * nach Bestätigung über die GETEILTEN Helfer aus `expenseDeriveShared.ts`.
 *
 * Buchungs-Vertrag (T-2 §6.2, BINDEND):
 *   1. „Buchen" ist gesperrt, solange ein `validationFlag` unbestätigt ist ODER
 *      der Nutzer für `needs_review` kein konkretes Scheme gewählt hat.
 *   2. Effektivwerte via `computeEffectiveVatOwedCents` / `computeEffectiveVatDeductible`
 *      / `computeEffectiveBreakdown` (wiederverwendet, nicht kopiert).
 *   3. Reihenfolge T-13/T-10: zuerst `createExpense(...)` MUSS erfolgreich sein,
 *      DANN `markScannedReceiptBooked(receipt.id, expenseId)`. Kein Status
 *      'booked' ohne existierenden Expense.
 *   4. §17-Pfad unverändert (negative Cents fließen aus dem Draft durch).
 *   5. Dedup-Warnung via `getExpenseForScannedReceipt` vor Buchung (S-1.6).
 *
 * M13b-Erweiterung: Bewirtungsbeleg-Toggle + §4-Abs.5-Pflichtfelder +
 * Trinkgeld-Eingabe + Buchen-Gate (§6 architecture-m13b.md).
 *
 * ❌ VETO-Leitplanke (§6.3): `computeEffective*`-Helfer UNVERÄNDERT (kein
 * bewirtung-Parameter). Vorsteuer bleibt 100 %. Trinkgeld läuft als separate
 * 0 %-Zeile (vatOwedCents===0). Toggle aus → Pfad bit-identisch zu heute.
 *
 * @module ReceiptScanModal
 */

import React, { useState, useEffect, useCallback, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ref, getDownloadURL } from 'firebase/storage';
import {
  IconClose,
  IconWarning,
  IconInfo,
  IconSuccess,
  IconRejected,
  IconHelp,
  IconSpinner,
} from '../../icons';

import { useAuth } from '../../context/AuthContext';
import { storage } from '../../firebaseConfig';
import { ScannedReceipt, ReceiptValidationFlag } from '../../types/scannedReceiptTypes';
import {
  TaxScheme,
  ExpenseDraft,
  CreateExpenseInput,
  ExpenseVatBreakdownEntry,
  BewirtungInfo,
} from '../../types/expenseTypes';
import { ExpenseSellerContext } from '../../utils/expenseTax';
import { mapScannedReceiptToExpenseDraft } from '../../utils/mapScannedReceiptToExpenseDraft';
import { createExpense } from '../../api/expensesApi';
import {
  markScannedReceiptBooked,
  getExpenseForScannedReceipt,
} from '../../api/scannedReceiptsApi';
import { centsToEuros } from '../../utils/moneyUtils';
import logger from '../../utils/logger';
import {
  computeEffectiveVatOwedCents,
  computeEffectiveVatDeductible,
  computeEffectiveBreakdown,
} from './expenseDeriveShared';
import {
  isBewirtungComplete,
  buildBewirtungInfo,
  appendTipLine,
  adjustTotalsForTip,
  BewirtungForm,
} from '../../utils/bewirtungBooking';

// ─── Constants ────────────────────────────────────────────────────────────────

const ALL_TAX_SCHEMES: TaxScheme[] = [
  'standard',
  'reverse_charge_13b',
  'intra_community_6a',
  'import_third_country',
  'small_business_19',
  'zero_exempt',
  'needs_review',
];

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReceiptScanModalProps {
  /** Whether the modal is visible. */
  open: boolean;
  /** The `status==='extracted'` scanned receipt to book as an expense. */
  receipt: ScannedReceipt;
  /** Called when the modal is dismissed without booking. */
  onClose: () => void;
  /**
   * Called after a successful booking with the new expense's Firestore document ID.
   * The parent should refresh its scan list (the receipt is now `status='booked'`).
   */
  onBooked: (expenseId: string) => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Formats integer cents as a EUR currency string (de-DE locale). */
function formatCents(cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(centsToEuros(cents));
}

/**
 * Parses a EUR-formatted string (e.g. "11,00" or "11.50") to integer cents.
 * Fail-closed: returns `null` for any invalid, empty-but-not-zero, or negative input.
 * Empty string → 0 (no tip).
 *
 * @param value User-entered EUR string.
 * @returns Integer cents, or `null` when unparseable / negative.
 */
function parseTipEuros(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const normalized = trimmed.replace(',', '.');
  const parsed = parseFloat(normalized);
  if (isNaN(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

// ─── ReceiptScanModal ──────────────────────────────────────────────────────────

/**
 * Confirmation modal converting a scanned receipt into a bookable expense.
 *
 * Pre-fills all fields from `mapScannedReceiptToExpenseDraft`. The user may
 * override the taxScheme (always) and, for §13b/IGL, the VAT rate (19/7 %).
 * Booking is blocked until every validation flag is acknowledged and a concrete
 * tax scheme is chosen — the human ALWAYS confirms (T-2 §6.2, no silent booking).
 *
 * M13b: optional Bewirtungsbeleg-Toggle (§6 architecture-m13b.md). Toggle off →
 * behavior is bit-identical to the pre-M13b code path.
 */
const ReceiptScanModal: React.FC<ReceiptScanModalProps> = ({
  open,
  receipt,
  onClose,
  onBooked,
}) => {
  const { t } = useTranslation(['common', 'expenses']);
  const { sellerData } = useAuth();

  // ── Compute the draft from the scanned receipt (E-2: vatDeductible=false) ──
  const sellerContext: ExpenseSellerContext = {
    isSmallBusiness: sellerData?.isSmallBusiness,
  };
  const draft: ExpenseDraft = mapScannedReceiptToExpenseDraft(receipt, sellerContext);

  // ── G-5: server-validierter Beleg-USt, UNABHÄNGIG vom Draft-Scheme ───────────
  // Der Adapter (T-2 §5) nullt `draft.vatOwedCents` für jedes Nicht-'standard'-Scheme —
  // auch für `needs_review`. Korrigiert der Mensch das Scheme auf 'standard', MUSS der
  // gebuchte Expense den ECHTEN, server-validierten Beleg-USt tragen
  // (`receipt.totalVatCents` / `normalizedBreakdown[].vatCents`), nicht die genullte
  // Draft-Zahl. Diese Basis wird hier scheme-unabhängig gehalten und an die geteilten
  // `computeEffective*`-Helfer als `originalVatOwedCents` übergeben; die Helfer gaten
  // sie weiterhin pro Scheme (needs_review/zero/small_business → 0, §13b → self-computed),
  // sodass nur der 'standard'-Pfad den echten Betrag verwendet.
  //
  // §17-Vorzeichen: Eine Gutschrift (documentTypeCode '381') trägt negative Cents —
  // exakt die im Adapter getroffene Vorzeichen-Entscheidung, hier über den bereits
  // festgelegten Belegart-Code gelesen (keine Neu-Ableitung der §17-Logik).
  const isCreditNote = draft.documentTypeCode === '381';
  const signServerCents = (cents: number | null): number =>
    isCreditNote ? -Math.abs(cents ?? 0) : (cents ?? 0);

  const serverVatOwedBaseCents = signServerCents(receipt.totalVatCents);
  // Pro Mehrsatz-Zeile (E-1): `normalizedBreakdown` ist 1:1-parallel zu `draft.vatBreakdown`
  // (der Adapter mappt beide aus derselben Quelle). Wir halten denselben Eintrag, ersetzen
  // aber `vatOwedCents` durch den server-validierten (signierten) Beleg-USt der Zeile.
  const serverBaseBreakdown: ExpenseVatBreakdownEntry[] = draft.vatBreakdown.map((entry, i) => ({
    ...entry,
    vatOwedCents: signServerCents(receipt.normalizedBreakdown[i]?.vatCents ?? null),
  }));

  const userIsFullyVatDeductible = sellerContext.isSmallBusiness === false;
  const validationFlags: ReceiptValidationFlag[] = receipt.validationFlags ?? [];
  const hasFlags = validationFlags.length > 0;

  // ── Local user-overrideable state ────────────────────────────────────────
  const [localTaxScheme, setLocalTaxScheme] = useState<TaxScheme>(draft.taxScheme);
  const [localVatRate, setLocalVatRate] = useState<19 | 7>(19);
  const [category, setCategory] = useState('');
  const [skrAccount, setSkrAccount] = useState('');
  const [flagsAcknowledged, setFlagsAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Image preview state (loaded from Storage storageRef) ─────────────────
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);

  // ── Dedup (S-1.6): an expense may already exist for this receipt ─────────
  const [alreadyBookedExpenseId, setAlreadyBookedExpenseId] = useState<string | null>(null);

  // ── M13b Bewirtungsbeleg — §4-Abs.5-State (§6.1 architecture-m13b.md) ────
  // `bewirtungOn` Default FALSE (fail-safe) — NICHT aus `receipt.isBewirtung` vorbelegt.
  // `receipt.isBewirtung` steuert nur das Info-Banner (schwacher Hint, §6.2).
  const [bewirtungOn, setBewirtungOn] = useState(false);
  const [art, setArt] = useState<'' | 'geschaeftlich_70' | 'betrieblich_100'>('');
  const [participants, setParticipants] = useState<string[]>(['']);
  const [occasion, setOccasion] = useState('');
  const [hostName, setHostName] = useState('');
  const [tipEuros, setTipEuros] = useState('');
  const [selfDeclaredOnReceipt, setSelfDeclaredOnReceipt] = useState(false);

  // Reset state when the receipt changes / modal re-opens.
  useEffect(() => {
    if (!open) return;
    const freshDraft = mapScannedReceiptToExpenseDraft(receipt, sellerContext);
    setLocalTaxScheme(freshDraft.taxScheme);
    setLocalVatRate(19);
    setCategory('');
    setSkrAccount('');
    setFlagsAcknowledged(false);
    setSubmitting(false);
    setImageUrl(null);
    setImageError(false);
    setAlreadyBookedExpenseId(null);
    // M13b bewirtung fields reset
    setBewirtungOn(false);
    setArt('');
    const participantHint = receipt.extraction?.participantsHint ?? '';
    const initParticipants = participantHint
      ? participantHint.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
    setParticipants(initParticipants.length > 0 ? initParticipants : ['']);
    setOccasion(receipt.extraction?.occasionHint ?? '');
    setHostName('');
    // Vorbefüllung Trinkgeld aus server-Kandidat (>0 Cent)
    const initTipEuros =
      receipt.tipCents != null && receipt.tipCents > 0
        ? (receipt.tipCents / 100).toFixed(2).replace('.', ',')
        : '';
    setTipEuros(initTipEuros);
    setSelfDeclaredOnReceipt(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, receipt.id]);

  // ── Load the receipt image from Storage ──────────────────────────────────
  useEffect(() => {
    if (!open || !receipt.storageRef) return;
    let cancelled = false;
    (async () => {
      try {
        const url = await getDownloadURL(ref(storage, receipt.storageRef));
        if (!cancelled) setImageUrl(url);
      } catch (err) {
        logger.error(err, 'ReceiptScanModal: failed to load receipt image');
        if (!cancelled) setImageError(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, receipt.storageRef]);

  // ── Dedup lookup before booking (S-1.6) ──────────────────────────────────
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const existing = await getExpenseForScannedReceipt(receipt.id);
        if (!cancelled && existing) setAlreadyBookedExpenseId(existing.id);
      } catch (err) {
        // A failed dedup lookup must NOT block the modal — log and continue.
        logger.warn('ReceiptScanModal: dedup lookup failed (non-blocking)', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, receipt.id]);

  // ── Derived / computed values (shared helpers — NOT copied) ──────────────
  const isReverseCharge =
    localTaxScheme === 'reverse_charge_13b' || localTaxScheme === 'intra_community_6a';

  const effectiveVatOwedCents = computeEffectiveVatOwedCents(
    localTaxScheme,
    localVatRate,
    draft.netCents,
    serverVatOwedBaseCents,
  );

  const effectiveVatDeductible = computeEffectiveVatDeductible(
    localTaxScheme,
    effectiveVatOwedCents,
    draft.completenessStatus,
    userIsFullyVatDeductible,
  );

  const effectiveVatDeductibleCents = effectiveVatDeductible ? effectiveVatOwedCents : 0;

  const effectiveBreakdown = computeEffectiveBreakdown(
    serverBaseBreakdown,
    localTaxScheme,
    localVatRate,
    effectiveVatDeductible,
  );

  // ── M13b: Bewirtungs-Form + Buchen-Gate (§6.4 architecture-m13b.md) ──────
  // tipCentsFromInput: null = ungültige/unparseable Eingabe (fail-closed).
  const tipCentsFromInput = parseTipEuros(tipEuros);
  // Rekonstruierter handschriftlicher Gesamtbetrag vom Server-Kandidaten.
  // Existiert nur wenn der Server eine Trinkgeld-Differenz erkennen konnte (receipt.tipCents != null).
  // Zweck: isTipPlausible prüft tipCents === handwrittenTotal − printedGross (Konsistenz-Constraint).
  const bewirtungHandwrittenTotal: number | null =
    receipt.tipCents != null ? receipt.tipCents + draft.grossCents : null;

  const bewirtungForm: BewirtungForm = {
    art,
    participants,
    occasion,
    hostName,
    // -1 als Sentinel für ungültige Eingabe → isTipPlausible gibt false zurück (fail-closed).
    tipCents: tipCentsFromInput ?? -1,
    handwrittenTotalCents: bewirtungHandwrittenTotal,
    selfDeclaredOnReceipt,
  };

  const bewirtungComplete =
    !bewirtungOn || isBewirtungComplete(bewirtungForm, draft.grossCents);

  // ── Booking-gate (T-2 §6.2.1) ────────────────────────────────────────────
  // Blocked while taxScheme is still needs_review OR any validation flag is
  // unacknowledged OR (M13b) bewirtung is on but incomplete.
  // Never silently booked.
  const schemeChosen = localTaxScheme !== 'needs_review';
  const flagsCleared = !hasFlags || flagsAcknowledged;
  const canBook = schemeChosen && flagsCleared && bewirtungComplete;

  // ── M13b: Fokussierte Mängelliste fehlender §4-Abs.5-Angaben (§6.4) ─────
  const bewirtungMissingFields: string[] = (() => {
    if (!bewirtungOn || bewirtungComplete) return [];
    const missing: string[] = [];
    if (art === '') missing.push(t('receiptScanModal.bewirtung.missingArt'));
    if (!participants.some((p) => p.trim() !== ''))
      missing.push(t('receiptScanModal.bewirtung.missingParticipants'));
    if (occasion.trim() === '') missing.push(t('receiptScanModal.bewirtung.missingOccasion'));
    if (draft.grossCents > 25_000 && hostName.trim() === '')
      missing.push(t('receiptScanModal.bewirtung.missingHostName'));
    // Trinkgeld: ungültige Eingabe oder Abweichung vom handschriftlichen Gesamt
    if (
      tipCentsFromInput === null ||
      tipCentsFromInput < 0 ||
      (bewirtungHandwrittenTotal !== null &&
        tipCentsFromInput !== bewirtungHandwrittenTotal - draft.grossCents)
    ) {
      missing.push(t('receiptScanModal.bewirtung.invalidTip'));
    }
    return missing;
  })();

  // ── Missing field labels for the §14 completeness banner ────────────────
  const missingFieldLabels = draft.missingMandatoryFields
    .map((field) => t(`expenses:missingFields.${field}`, field))
    .join(', ');

  // ── M13b Bewirtungsbeleg — Handlers ──────────────────────────────────────

  /**
   * Toggles the Bewirtung section.
   * When enabled and `skrAccount` is empty, auto-fills SKR-03 account 4650
   * as a suggestion (editierbar, nicht erzwungen — §8 architecture-m13b.md).
   * The exact account number is subject to 🧮 STB-CHECK (Pre-Live gate).
   */
  const handleBewirtungToggle = (checked: boolean) => {
    setBewirtungOn(checked);
    if (checked && !skrAccount.trim()) {
      setSkrAccount('4650');
    }
  };

  const handleParticipantChange = (index: number, value: string) => {
    setParticipants((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddParticipant = () => {
    setParticipants((prev) => [...prev, '']);
  };

  const handleRemoveParticipant = (index: number) => {
    setParticipants((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : ['']));
  };

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleTaxSchemeChange = useCallback((scheme: TaxScheme) => {
    setLocalTaxScheme(scheme);
    if (scheme === 'reverse_charge_13b' || scheme === 'intra_community_6a') {
      setLocalVatRate(19);
    }
  }, []);

  const handleBook = useCallback(async () => {
    if (submitting || !canBook) return;

    setSubmitting(true);
    try {
      // Re-compute effective figures via the shared helpers (T-2 §6.2.2).
      // G-5: server-validierte Basis statt der vom needs_review-Overlay genullten
      // draft.vatOwedCents / draft.vatBreakdown (siehe Berechnung oben).
      const effOwed = computeEffectiveVatOwedCents(
        localTaxScheme,
        localVatRate,
        draft.netCents,
        serverVatOwedBaseCents,
      );
      const effDeduct = computeEffectiveVatDeductible(
        localTaxScheme,
        effOwed,
        draft.completenessStatus,
        userIsFullyVatDeductible,
      );
      const effBreakdown = computeEffectiveBreakdown(
        serverBaseBreakdown,
        localTaxScheme,
        localVatRate,
        effDeduct,
      );

      // ── M13b: ❌ VETO-Leitplanke — bewirtung-Pfad (§6.3 architecture-m13b.md) ──
      // computeEffective*-Helfer UNVERÄNDERT aufgerufen (kein bewirtung-Parameter).
      // Die Vorsteuer bleibt 100 % — KEIN Pfad kürzt irgendein vat*-Feld auf 70 %.
      // Trinkgeld-Zeile + Totals-Anpassung ist ADDITIV danach, berührt KEINE vat*-Felder.
      // Toggle aus → Pfad BIT-IDENTISCH zu heute (keine netCents/grossCents-Überschreibung
      // außer mit denselben draft-Werten, kein bewirtung-Objekt).
      let finalBreakdown = effBreakdown;
      let finalNetCents = draft.netCents;
      let finalGrossCents = draft.grossCents;
      let bewirtungForInput: BewirtungInfo | undefined;

      if (bewirtungOn) {
        const bewirtungInfo = buildBewirtungInfo(bewirtungForm);
        // 0 %-Trinkgeld-Zeile anhängen (nur bei tipCents > 0); vatOwedCents/vatDeductibleCents = 0
        finalBreakdown = appendTipLine(effBreakdown, bewirtungInfo.tipCents);
        // Trinkgeld zu netCents + grossCents addieren; vatOwedCents bleibt unberührt
        const adjusted = adjustTotalsForTip(
          draft.netCents,
          draft.grossCents,
          bewirtungInfo.tipCents,
        );
        finalNetCents = adjusted.netCents;
        finalGrossCents = adjusted.grossCents;
        bewirtungForInput = bewirtungInfo;
      }

      const input: CreateExpenseInput = {
        ...draft,
        // M13b: ggf. um Trinkgeld-adjustierte Totals (bei bewirtungOn=false: identisch mit draft)
        netCents: finalNetCents,
        grossCents: finalGrossCents,
        vatOwedCents: effOwed,
        vatDeductible: effDeduct,
        vatDeductibleCents: effDeduct ? effOwed : 0,
        vatBreakdown: finalBreakdown,
        vatRate: isReverseCharge ? localVatRate : draft.vatRate,

        taxScheme: localTaxScheme,
        // Confirmed because the human reviewed the modal and clicked "Buchen".
        taxSchemeConfirmedByUser: true,

        category: category.trim() || null,
        skrAccount: skrAccount.trim() || null,

        status: 'booked',
        // M13b: bewirtung{} nur wenn Toggle an (additiv, Altbestand trägt es nicht)
        ...(bewirtungForInput ? { bewirtung: bewirtungForInput } : {}),
      };

      // T-13/T-10 ordering (BINDEND): createExpense MUST succeed FIRST. Only then
      // is the receipt flipped to 'booked' — no 'booked' status without an Expense.
      const expenseId = await createExpense(input);
      await markScannedReceiptBooked(receipt.id, expenseId);

      toast.success(t('receiptScanModal.success'));
      onBooked(expenseId);
    } catch (err) {
      logger.error(err, 'ReceiptScanModal: booking failed');
      toast.error(t('receiptScanModal.errorSave'));
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    canBook,
    draft,
    serverVatOwedBaseCents,
    serverBaseBreakdown,
    localTaxScheme,
    localVatRate,
    userIsFullyVatDeductible,
    isReverseCharge,
    category,
    skrAccount,
    receipt.id,
    t,
    onBooked,
    // M13b bewirtung deps
    bewirtungOn,
    bewirtungForm,
  ]);

  if (!open) return null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="receipt-scan-modal-title"
    >
      <div
        className="w-full max-w-4xl bg-surface-raised border border-hairline rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden"
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2 id="receipt-scan-modal-title" className="text-primary font-semibold text-base">
            {t('receiptScanModal.title')}
          </h2>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-muted hover:text-primary transition-colors disabled:opacity-40 p-1 -mr-1"
            aria-label={t('receiptScanModal.cancel')}
          >
            <IconClose />
          </button>
        </div>

        {/* ── Body: image + fields side by side ─────────────────────────────── */}
        <div className="overflow-y-auto flex-1 grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* ── Image column ───────────────────────────────────────────────── */}
          <div className="bg-black/30 border-b md:border-b-0 md:border-r border-hairline flex items-center justify-center p-4 min-h-[200px]">
            {imageError ? (
              <div className="flex flex-col items-center gap-2 text-faint text-sm">
                <IconRejected />
                <span>{t('receiptScanModal.imageError')}</span>
              </div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt={t('receiptScanModal.imageAlt')}
                className="max-w-full max-h-[70vh] object-contain rounded-lg"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-faint text-sm">
                <IconSpinner className="animate-spin" />
                <span>{t('receiptScanModal.imageLoading')}</span>
              </div>
            )}
          </div>

          {/* ── Fields column ──────────────────────────────────────────────── */}
          <div className="px-6 py-5 space-y-5">
            {/* Dedup warning (S-1.6) */}
            {alreadyBookedExpenseId && (
              <div className="flex items-start gap-2 bg-[var(--tone-caution-bg)] border border-[var(--tone-caution-border)] rounded-lg px-3 py-2.5 text-[var(--tone-caution-fg)] text-xs">
                <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('receiptScanModal.dedupWarning')}</span>
              </div>
            )}

            {/* credit_note §17 notice */}
            {draft.documentTypeCode === '381' && (
              <div className="flex items-start gap-2 bg-[var(--tone-special-bg)] border border-[var(--tone-special-border)] rounded-lg px-3 py-2.5 text-[var(--tone-special-fg)] text-xs">
                <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('receiptScanModal.creditNoteNotice')}</span>
              </div>
            )}

            {/* Non-EUR warning */}
            {draft.nonEurCurrency && (
              <div className="flex items-start gap-2 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded-lg px-3 py-2.5 text-[var(--tone-warning-fg)] text-xs">
                <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('receiptScanModal.nonEurWarning', { currency: draft.nonEurCurrency })}</span>
              </div>
            )}

            {/* needs_review taxScheme warning */}
            {localTaxScheme === 'needs_review' && (
              <div className="flex items-start gap-2 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-lg px-3 py-2.5 text-[var(--tone-danger-fg)] text-xs">
                <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('receiptScanModal.needsReviewWarning')}</span>
              </div>
            )}

            {/* §14 completeness banner (neutral wording) */}
            {draft.completenessStatus === 'incomplete' && (
              <div className="flex items-start gap-2 bg-[var(--tone-caution-bg)] border border-[var(--tone-caution-border)] rounded-lg px-3 py-2.5 text-[var(--tone-caution-fg)] text-xs">
                <IconWarning size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('receiptScanModal.incompleteWarning', { fields: missingFieldLabels || '—' })}</span>
              </div>
            )}

            {/* Validation flags — must be acknowledged before booking */}
            {hasFlags && (
              <div className="bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2.5 text-xs text-[var(--tone-info-fg)] space-y-2">
                <div className="flex items-start gap-2">
                  <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{t('receiptScanModal.flagsWarning')}</span>
                </div>
                <ul className="list-disc list-inside space-y-0.5 text-[var(--tone-info-fg)]">
                  {validationFlags.map((flag) => (
                    <li key={flag}>{t(`receiptScanModal.flag.${flag}`, flag)}</li>
                  ))}
                </ul>
                <label className="flex items-center gap-2 cursor-pointer text-[var(--tone-info-fg)] pt-1">
                  <input
                    type="checkbox"
                    checked={flagsAcknowledged}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setFlagsAcknowledged(e.target.checked)}
                    disabled={submitting}
                    className="rounded border-gray-500"
                  />
                  <span>{t('receiptScanModal.confirmFlagsLabel')}</span>
                </label>
              </div>
            )}

            {/* Supplier info */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-faint text-xs mb-0.5">{t('receiptScanModal.supplier')}</p>
                <p className="text-primary font-medium">{draft.supplierName || '—'}</p>
              </div>
              <div>
                <p className="text-faint text-xs mb-0.5">{t('receiptScanModal.invoiceNumber')}</p>
                <p className="text-primary font-mono text-xs">{draft.invoiceNumber || '—'}</p>
              </div>
              <div>
                <p className="text-faint text-xs mb-0.5">{t('receiptScanModal.invoiceDate')}</p>
                <p className="text-primary text-xs">{draft.expenseDate || '—'}</p>
              </div>
              {draft.serviceDate && (
                <div>
                  <p className="text-faint text-xs mb-0.5">{t('receiptScanModal.serviceDate')}</p>
                  <p className="text-primary text-xs">{draft.serviceDate}</p>
                </div>
              )}
            </div>

            <hr className="border-hairline" />

            {/* Amounts */}
            <div>
              <p className="text-muted text-xs font-medium uppercase tracking-wide mb-2">
                {t('receiptScanModal.amounts')}
              </p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-muted">{t('receiptScanModal.net')}</span>
                  <span className="text-primary font-mono">{formatCents(draft.netCents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">
                    {t('receiptScanModal.vatOwed')}
                    {isReverseCharge && (
                      <span className="text-[var(--tone-special-fg)] ml-1 text-xs">(§ 13b/igE)</span>
                    )}
                  </span>
                  <span className="text-primary font-mono">{formatCents(effectiveVatOwedCents)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted">{t('receiptScanModal.vatDeductibleLabel')}</span>
                  <span className={`font-mono ${effectiveVatDeductible ? 'text-[var(--tone-success-fg)]' : 'text-faint'}`}>
                    {formatCents(effectiveVatDeductibleCents)}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-semibold border-t border-hairline pt-1.5">
                  <span className="text-secondary">{t('receiptScanModal.gross')}</span>
                  <span className="text-primary font-mono">{formatCents(draft.grossCents)}</span>
                </div>
                {draft.roundingDiffCents !== 0 && (
                  <div className="flex justify-between text-xs text-[var(--tone-warning-fg)]">
                    <span>{t('receiptScanModal.roundingDiff')}</span>
                    <span className="font-mono">{formatCents(draft.roundingDiffCents)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Multi-rate VAT breakdown */}
            {effectiveBreakdown.length > 1 && (
              <div>
                <p className="text-muted text-xs font-medium uppercase tracking-wide mb-2">
                  {t('receiptScanModal.multiRateTitle')}
                </p>
                <table className="w-full text-xs text-secondary">
                  <thead>
                    <tr className="text-faint text-left">
                      <th className="pb-1 font-normal">{t('receiptScanModal.vatBreakdown.categoryCode')}</th>
                      <th className="pb-1 font-normal text-right">{t('receiptScanModal.vatBreakdown.rate')}</th>
                      <th className="pb-1 font-normal text-right">{t('receiptScanModal.vatBreakdown.net')}</th>
                      <th className="pb-1 font-normal text-right">{t('receiptScanModal.vatBreakdown.owed')}</th>
                      <th className="pb-1 font-normal text-right">{t('receiptScanModal.vatBreakdown.deductible')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {effectiveBreakdown.map((b, i) => (
                      <tr key={i} className="border-t border-hairline">
                        <td className="py-1 font-mono">{b.categoryCode || '—'}</td>
                        <td className="py-1 text-right">{b.taxRate} %</td>
                        <td className="py-1 text-right font-mono">{formatCents(b.netCents)}</td>
                        <td className="py-1 text-right font-mono">{formatCents(b.vatOwedCents)}</td>
                        <td className={`py-1 text-right font-mono ${b.vatDeductibleCents > 0 ? 'text-[var(--tone-success-fg)]' : 'text-faint'}`}>
                          {formatCents(b.vatDeductibleCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <hr className="border-hairline" />

            {/* Tax scheme selector */}
            <div>
              <label className="block text-xs text-muted mb-1" htmlFor="scanTaxSchemeSelect">
                {t('receiptScanModal.taxSchemeLabel')}
              </label>
              <select
                id="scanTaxSchemeSelect"
                value={localTaxScheme}
                onChange={(e: ChangeEvent<HTMLSelectElement>) => handleTaxSchemeChange(e.target.value as TaxScheme)}
                disabled={submitting}
                className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 cursor-pointer"
              >
                {ALL_TAX_SCHEMES.map((scheme) => (
                  <option key={scheme} value={scheme}>
                    {t(`expenses:taxScheme.${scheme}`)}
                  </option>
                ))}
              </select>
              <p className="text-faint text-xs mt-1">{t('receiptScanModal.taxSchemeHelp')}</p>
            </div>

            {/* VAT rate selector — only for §13b/IGL */}
            {isReverseCharge && (
              <div>
                <label className="block text-xs text-muted mb-1" htmlFor="scanVatRateSelect">
                  {t('receiptScanModal.vatRateLabel')}
                </label>
                <select
                  id="scanVatRateSelect"
                  value={localVatRate}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => setLocalVatRate(Number(e.target.value) as 19 | 7)}
                  disabled={submitting}
                  className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 cursor-pointer"
                >
                  <option value={19}>{t('receiptScanModal.vatRate19')}</option>
                  <option value={7}>{t('receiptScanModal.vatRate7')}</option>
                </select>
                <p className="text-faint text-xs mt-1">{t('receiptScanModal.vatRateHelp')}</p>
              </div>
            )}

            {/* vatDeductible indicator */}
            <div className={`flex items-center gap-2 text-xs rounded-lg px-3 py-2 border ${
              effectiveVatDeductible
                ? 'bg-[var(--tone-success-bg)] border-[var(--tone-success-border)] text-[var(--tone-success-fg)]'
                : 'bg-[rgb(var(--surface-raised-rgb)/0.6)] border-hairline text-muted'
            }`}>
              {effectiveVatDeductible ? (
                <IconSuccess className="flex-shrink-0" />
              ) : (
                <IconRejected className="flex-shrink-0" />
              )}
              <span>
                {effectiveVatDeductible
                  ? t('receiptScanModal.vatDeductibleInfo')
                  : sellerContext.isSmallBusiness
                    ? t('receiptScanModal.smallBusinessNotice')
                    : t('receiptScanModal.vatNotDeductibleInfo')}
              </span>
            </div>

            <hr className="border-hairline" />

            {/* ── M13b Bewirtungsbeleg — Toggle + §4-Felder (§6.2 architecture-m13b.md) ── */}

            {/* Info-Banner: server-seitiger Hint (rein informativ, steuert NICHT den Toggle) */}
            {(receipt.isBewirtung ?? false) && !bewirtungOn && (
              <div className="flex items-start gap-2 bg-[var(--tone-info-bg)] border border-[var(--tone-info-border)] rounded-lg px-3 py-2.5 text-[var(--tone-info-fg)] text-xs">
                <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                <span>{t('receiptScanModal.bewirtung.infoBanner')}</span>
              </div>
            )}

            {/* Toggle: maßgebliche Bewirtungsbeleg-Klassifikation (Default false, fail-safe) */}
            <label className="flex items-center gap-2 cursor-pointer text-sm text-secondary">
              <input
                type="checkbox"
                checked={bewirtungOn}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleBewirtungToggle(e.target.checked)
                }
                disabled={submitting}
                className="rounded border-gray-500"
              />
              <span>{t('receiptScanModal.bewirtung.toggleLabel')}</span>
            </label>

            {/* §4-Abs.5-Felder (nur sichtbar wenn Toggle an) */}
            {bewirtungOn && (
              <div className="space-y-4 border border-[var(--tone-accent-border)] rounded-lg px-4 py-4 bg-[var(--tone-accent-bg)]">

                {/* Bewirtungsart — Pflicht-Radio, KEIN Default (fail-closed) */}
                <div>
                  <p className="text-xs text-muted mb-2">
                    {t('receiptScanModal.bewirtung.artLabel')}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    {(['geschaeftlich_70', 'betrieblich_100'] as const).map((option) => (
                      <label
                        key={option}
                        className="flex items-center gap-2 text-sm text-secondary cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="bewirtungArt"
                          value={option}
                          checked={art === option}
                          onChange={() => setArt(option)}
                          disabled={submitting}
                          className="border-gray-500"
                        />
                        <span>{t(`receiptScanModal.bewirtung.art.${option}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Teilnehmer (≥1 nicht-leerer Eintrag Pflicht) */}
                <div>
                  <p className="text-xs text-muted mb-1">
                    {t('receiptScanModal.bewirtung.participantsLabel')}
                  </p>
                  <div className="space-y-1.5">
                    {participants.map((p, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={p}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            handleParticipantChange(i, e.target.value)
                          }
                          disabled={submitting}
                          placeholder={t('receiptScanModal.bewirtung.participantPlaceholder')}
                          className="flex-1 bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-1.5 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
                        />
                        {participants.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveParticipant(i)}
                            disabled={submitting}
                            className="text-faint hover:text-[var(--tone-danger-fg)] transition-colors disabled:opacity-40 p-1"
                            aria-label={t('receiptScanModal.bewirtung.removeParticipant')}
                          >
                            <IconClose size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddParticipant}
                      disabled={submitting}
                      className="text-xs text-accent-ink hover:text-accent-ink transition-colors disabled:opacity-40 mt-1"
                    >
                      + {t('receiptScanModal.bewirtung.addParticipant')}
                    </button>
                  </div>
                </div>

                {/* Anlass (non-empty Pflicht) */}
                <div>
                  <label
                    className="block text-xs text-muted mb-1"
                    htmlFor="bewirtungOccasion"
                  >
                    {t('receiptScanModal.bewirtung.occasionLabel')}
                  </label>
                  <input
                    id="bewirtungOccasion"
                    type="text"
                    value={occasion}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setOccasion(e.target.value)}
                    disabled={submitting}
                    placeholder={t('receiptScanModal.bewirtung.occasionPlaceholder')}
                    className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-1.5 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
                  />
                </div>

                {/* Name des Bewirtenden (Pflicht nur wenn grossCents > 25 000 = 250 €) */}
                <div>
                  <label
                    className="block text-xs text-muted mb-1"
                    htmlFor="bewirtungHostName"
                  >
                    {t('receiptScanModal.bewirtung.hostNameLabel')}
                    {draft.grossCents > 25_000 && (
                      <span className="text-[var(--tone-danger-fg)] ml-1">
                        {t('receiptScanModal.bewirtung.hostNameRequired')}
                      </span>
                    )}
                  </label>
                  <input
                    id="bewirtungHostName"
                    type="text"
                    value={hostName}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setHostName(e.target.value)}
                    disabled={submitting}
                    placeholder={t('receiptScanModal.bewirtung.hostNamePlaceholder')}
                    className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-1.5 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
                  />
                </div>

                {/* Trinkgeld (EUR, vorbefüllt aus receipt.tipCents, manuell bestätigt) */}
                <div>
                  <label
                    className="block text-xs text-muted mb-1"
                    htmlFor="bewirtungTip"
                  >
                    {t('receiptScanModal.bewirtung.tipLabel')}
                  </label>
                  <input
                    id="bewirtungTip"
                    type="text"
                    value={tipEuros}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setTipEuros(e.target.value)}
                    disabled={submitting}
                    placeholder={t('receiptScanModal.bewirtung.tipPlaceholder')}
                    className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-1.5 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
                  />
                </div>

                {/* selfDeclaredOnReceipt — GoBD-Nachweis */}
                <label className="flex items-start gap-2 cursor-pointer text-xs text-secondary">
                  <input
                    type="checkbox"
                    checked={selfDeclaredOnReceipt}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSelfDeclaredOnReceipt(e.target.checked)
                    }
                    disabled={submitting}
                    className="mt-0.5 rounded border-gray-500"
                  />
                  <span>{t('receiptScanModal.bewirtung.selfDeclaredLabel')}</span>
                </label>

                {/* GoBD-Hinweis (🧑‍⚖️ JURIST-Wortlaut, Pre-Live-Gate) */}
                <div className="flex items-start gap-2 bg-[var(--tone-warning-bg)] border border-[var(--tone-warning-border)] rounded px-3 py-2 text-[var(--tone-warning-fg)] text-xs">
                  <IconInfo size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{t('receiptScanModal.bewirtung.gobdHint')}</span>
                </div>

                {/* SKR-Konto-Vorschlag (setzt bestehendes skrAccount-Feld; editierbar, §8) */}
                <p className="text-xs text-faint">
                  {t('receiptScanModal.bewirtung.skrHint')}
                </p>

                {/* Fokussierte Mängelliste fehlender §4-Abs.5-Angaben */}
                {bewirtungMissingFields.length > 0 && (
                  <div className="bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded px-3 py-2.5 text-xs text-[var(--tone-danger-fg)] space-y-1">
                    <p className="font-medium">{t('receiptScanModal.bewirtung.incomplete')}</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      {bewirtungMissingFields.map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Optional fields: category + skrAccount */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-muted mb-1" htmlFor="scanExpenseCategory">
                  {t('receiptScanModal.categoryLabel')}
                </label>
                <input
                  id="scanExpenseCategory"
                  type="text"
                  value={category}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setCategory(e.target.value)}
                  disabled={submitting}
                  placeholder={t('receiptScanModal.categoryPlaceholder')}
                  className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
                />
              </div>
              <div>
                <label className="block text-xs text-muted mb-1" htmlFor="scanExpenseSkrAccount">
                  {t('receiptScanModal.skrAccountLabel')}
                </label>
                <input
                  id="scanExpenseSkrAccount"
                  type="text"
                  value={skrAccount}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setSkrAccount(e.target.value)}
                  disabled={submitting}
                  placeholder={t('receiptScanModal.skrAccountPlaceholder')}
                  className="w-full bg-surface-raised border border-strong focus:border-indigo-500 rounded-lg px-3 py-2 text-primary text-sm outline-none transition-colors disabled:opacity-50 placeholder:text-faint"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer / actions ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-hairline bg-[rgb(var(--surface-base-rgb)/0.4)]">
          <span className="text-xs text-faint">
            {!canBook && t('receiptScanModal.bookBlockedHint')}
          </span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm text-secondary hover:text-primary transition-colors disabled:opacity-40"
            >
              {t('receiptScanModal.cancel')}
            </button>
            <button
              type="button"
              onClick={handleBook}
              disabled={submitting || !canBook}
              className="px-5 py-2 text-sm rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {!canBook && <IconHelp size={12} />}
              {submitting ? t('receiptScanModal.saving') : t('receiptScanModal.book')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptScanModal;
